import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { Plus, Minus, Star, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { img_url } from '../../api/index';
import { useQrLanguage } from './LanguageContext';

export interface QrProduct {
  id: number | string;
  name: string;
  description?: string;
  price: number | string;
  image?: string | null;
  category?: string | number;
  categoryName?: string;
  rating?: number;
  isNew?: boolean;
  isFeatured?: boolean;
  isPopular?: boolean;
  details?: any[];
  stocks?: any[];
  detail_id?: number | string | null;
}

interface ProductCardProps {
  product: QrProduct;
  onAddToCart: (product: QrProduct) => void;
  onClick?: (product: QrProduct) => void;
  isLarge?: boolean;
  currency?: string;
  /**
   * Current quantity of this product in the cart. When > 0 the bottom-right
   * "+" button morphs into a compact pill containing [-] qty [+]. After 3s
   * of inactivity it collapses back to just "+" so the card stays clean.
   */
  cartQuantity?: number;
  /**
   * Called with +1 / -1 when the user taps the in-card +/- buttons. The parent
   * is responsible for translating this into onAddToCart / onUpdateQuantity
   * calls (so cart-line bookkeeping stays centralized).
   */
  onQuantityChange?: (product: QrProduct, delta: number) => void;
  /**
   * Favorit / kalp ikonu state'i. Parent (CustomerFavoritesContext) authoritative;
   * ProductCard kartı sadece bu prop'a bakar — kendi local state'i tutmaz.
   * Tanımlı değilse kalp ikonu salt-okunur görünür (toggle yapılmaz).
   */
  isFavorited?: boolean;
  /**
   * Kalp ikonuna tıklanınca çağrılır. Promise dönerse kart içinde kısa bir loading
   * pulse gösterir; throw eden bir promise UI'da geri alınır (optimistic'e
   * güvendiğimiz için handler kendi içinde uyarı göstermeli — ProductCard
   * sessizce eski state'e döner).
   */
  onToggleFavorite?: (product: QrProduct) => void | Promise<void>;
}

// Light haptic feedback helper (no-op on iOS Safari, works on Android Chrome)
const haptic = (ms = 40) => {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      (navigator as any).vibrate(ms);
    }
  } catch {
    /* noop */
  }
};

const getImageUrl = (img?: string | null) => {
  if (!img) return '';
  return img.startsWith('http') ? img : `${img_url}/${img}`;
};

const formatPrice = (price: number | string) => {
  const n = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(n)) return '0';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

// We removed the auto-collapse logic to match Native apps where 
// items in cart permanently show [-] N [+]. This fixes state bugs.

function ProductCardInner({
  product,
  onAddToCart,
  onClick,
  isLarge = false,
  currency = '₼',
  cartQuantity = 0,
  onQuantityChange,
  isFavorited,
  onToggleFavorite,
}: ProductCardProps) {
  const { t } = useQrLanguage();
  // Optimistic local override — parent state'i bir frame geç gelse bile kalp
  // anında doluyor. Parent'tan gelen gerçek değer setlenince override silinir.
  const [optimisticFav, setOptimisticFav] = useState<boolean | null>(null);
  const [favPending, setFavPending] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Parent'tan gelen gerçek değer optimistic ile eşleşince örtüsüz düz state'e dön.
  useEffect(() => {
    if (optimisticFav != null && optimisticFav === !!isFavorited) {
      setOptimisticFav(null);
    }
  }, [isFavorited, optimisticFav]);

  const isFavorite =
    optimisticFav != null ? optimisticFav : !!isFavorited;

  // Pill expansion state is completely local for maximum responsiveness
  const [isPillExpanded, setIsPillExpanded] = useState(false);
  const collapseTimerRef = useRef<NodeJS.Timeout | null>(null);

  const imgUrl = getImageUrl(product.image);
  const rating = typeof product.rating === 'number' ? product.rating : 4.5;
  const isInCart = cartQuantity > 0;

  const resetCollapseTimer = () => {
    if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = setTimeout(() => {
      setIsPillExpanded(false);
    }, 2000);
  };

  const handlePlus = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptic(8);

    // Expand instantly and reset the 2s auto-collapse timer
    setIsPillExpanded(true);
    resetCollapseTimer();

    // Defer the heavy Redux update to the next frame to prevent UI stutter
    setTimeout(() => {
      if (onQuantityChange) onQuantityChange(product, 1);
      else onAddToCart(product);
    }, 50);
  };

  const handleMinus = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptic(6);

    if (cartQuantity <= 1) {
      // If we are removing the last item, collapse instantly and clear timer
      setIsPillExpanded(false);
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    } else {
      // Otherwise, just reset the 2s timer
      resetCollapseTimer();
    }

    // Defer the heavy Redux update
    setTimeout(() => {
      if (onQuantityChange) onQuantityChange(product, -1);
    }, 50);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    };
  }, []);

  return (
    <motion.div
      onClick={() => onClick && onClick(product)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={onClick ? { scale: 0.98 } : undefined}
      transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      style={{ willChange: 'transform' }}
      className={`relative bg-white dark:bg-[#121214] rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] border border-gray-100 dark:border-white/5 transition-colors group flex flex-col ${onClick ? 'cursor-pointer' : ''} ${isLarge ? 'w-full' : 'h-full'} qrm-no-select`}
    >
      <div
        className={`relative overflow-hidden bg-gray-100 dark:bg-gray-800 ${isLarge ? 'aspect-[16/10]' : 'aspect-[5/4]'
          }`}
      >
        {imgUrl && !imgError ? (
          <img
            src={imgUrl}
            alt={product.name}
            onError={() => setImgError(true)}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30">
            <span className="text-4xl">🍽️</span>
          </div>
        )}

        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.isFeatured && (
            <span className="px-2.5 py-1 bg-black/70 dark:bg-black/60 backdrop-blur-md text-white text-[10px] font-bold tracking-wider uppercase rounded-lg shadow-sm">
              {t.popular}
            </span>
          )}
          {product.isNew && (
            <span className="px-2.5 py-1 bg-orange-500/90 backdrop-blur-md text-white text-[10px] font-bold tracking-wider uppercase rounded-lg shadow-sm">
              {t.newBadge}
            </span>
          )}
        </div>

        {/* Favorite Button — top-right of the image */}
        <motion.button
          key="fav"
          type="button"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 420, damping: 28 }}
          onClick={async (e) => {
            e.stopPropagation();
            if (favPending || !onToggleFavorite) return;
            haptic(6);
            const prev = isFavorite;
            // Optimistic flip — gerçek değer parent'tan gelene kadar.
            setOptimisticFav(!prev);
            setFavPending(true);
            try {
              await onToggleFavorite(product);
            } catch {
              // Geri al — parent uyarısı island.show ile gösterir.
              setOptimisticFav(prev);
            } finally {
              setFavPending(false);
            }
          }}
          whileTap={{ scale: 0.85 }}
          disabled={favPending}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 dark:bg-black/40 backdrop-blur-md flex items-center justify-center shadow-sm z-10 transition-opacity ${
            favPending ? 'opacity-70' : ''
          }`}
          aria-label={isFavorite ? 'unfavorite' : 'favorite'}
          aria-pressed={isFavorite}
        >
          <Heart
            className={`w-4 h-4 transition-colors ${
              isFavorite
                ? 'fill-red-500 text-red-500'
                : 'text-gray-700 dark:text-white'
            }`}
          />
        </motion.button>
      </div>

      <div className={`p-3.5 flex flex-col flex-1 ${isLarge ? 'gap-3' : 'gap-1.5'}`}>
        <div className="flex justify-between items-start gap-2">
          <h3
            className={`font-bold text-gray-900 dark:text-white line-clamp-2 ${isLarge ? 'text-lg' : 'text-[14px] leading-snug'
              }`}
          >
            {product.name}
          </h3>
          <div className="flex items-center gap-1 text-[10px] font-bold bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded-md shrink-0 text-gray-900 dark:text-white">
            <Star className="w-2.5 h-2.5 text-orange-500 fill-orange-500" />
            <span>{rating.toFixed(1)}</span>
          </div>
        </div>

        {product.description ? (
          <p
            className={`text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed ${isLarge ? 'text-sm mb-2' : 'text-[12px] mb-1'
              }`}
          >
            {product.description}
          </p>
        ) : null}

        {/* Bottom row: price only */}
        <div className="flex items-center justify-between mt-auto pt-1 min-h-[44px]">
          <span
            className={`${isLarge ? 'text-xl' : 'text-[16px]'
              } font-extrabold text-orange-500 tracking-tight`}
          >
            {currency}
            {formatPrice(product.price)}
          </span>
        </div>
      </div>

      {/* Wolt-Style Add Button (Absolute Bottom-Right of Card) */}
      <div className="absolute bottom-0 right-0 z-20">
        <motion.div
          initial={false}
          animate={{ width: isPillExpanded ? 118 : 44 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className="flex flex-row-reverse items-center overflow-hidden transition-colors duration-300 bg-orange-500 text-white"
          style={{
            height: 44,
            willChange: 'width',
            borderTopLeftRadius: '36px',
            borderBottomRightRadius: '24px' // Matches card's rounded-3xl
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <motion.button
            type="button"
            whileTap={{ scale: 0.85 }}
            onClick={handlePlus}
            className="w-11 h-11 flex items-center justify-center shrink-0 z-10"
            aria-label="add to cart"
          >
            <Plus className="w-5 h-5" strokeWidth={3} />
          </motion.button>

          <AnimatePresence>
            {isPillExpanded && (
              <motion.div
                key="count-container"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex items-center justify-center w-[30px] shrink-0"
              >
                <span className="text-[15px] font-extrabold tracking-tight tabular-nums text-white">
                  {cartQuantity}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {isPillExpanded && (
              <motion.button
                key="minus-btn"
                type="button"
                initial={{ opacity: 0, scale: 0.5, x: 10 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.5, x: 10 }}
                transition={{ duration: 0.2 }}
                whileTap={{ scale: 0.85 }}
                onClick={handleMinus}
                className="w-11 h-11 flex items-center justify-center shrink-0 z-10"
                aria-label="decrease quantity"
              >
                <Minus className="w-5 h-5" strokeWidth={3} />
              </motion.button>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}

// React.memo cuts re-renders when the parent list re-renders but this card's
// props are unchanged — massive win on big product grids.
const ProductCard = memo(ProductCardInner);
export default ProductCard;
